import api from './api';

export interface SalesSummary {
  total_sales: number;
  total_revenue: number;
  average_ticket: number;
  total_items_sold: number;
}

export interface SalesByPeriod {
  period: string;
  total_sales: number;
  total_revenue: number;
}

export interface SalesByPaymentMethod {
  payment_method: string;
  total_sales: number;
  total_revenue: number;
  percentage: number;
}

export interface TopSellingProduct {
  product_id: number;
  product_name: string;
  category_name: string | null;
  quantity_sold: number;
  total_revenue: number;
}

export interface SalesByCategory {
  category_id: number | null;
  category_name: string;
  total_sales: number;
  total_revenue: number;
  percentage: number;
}

export interface LowStockProduct {
  id: number;
  name: string;
  category_name: string | null;
  stock: number;
  minimum_stock: number;
  stock_ratio: number;
}

export interface NewPatientsByPeriod {
  period: string;
  count: number;
}

export interface PatientsBySpecies {
  species: string;
  count: number;
  percentage: number;
}

export interface ServicesSummary {
  total_consultations: number;
  total_vaccinations: number;
  total_dewormings: number;
  total_grooming: number;
}

export interface GroomingCancellationByOwner {
  owner_id: number;
  owner_name: string;
  owner_phone: string | null;
  cancellation_count: number;
}

export interface DashboardStats {
  sales_summary: SalesSummary;
  sales_by_period: SalesByPeriod[];
  sales_by_payment_method: SalesByPaymentMethod[];
  top_selling_products: TopSellingProduct[];
  sales_by_category: SalesByCategory[];
  low_stock_products: LowStockProduct[];
  new_patients_by_period: NewPatientsByPeriod[];
  patients_by_species: PatientsBySpecies[];
  services_summary: ServicesSummary;
}

interface DateRangeParams {
  start_date?: string;
  end_date?: string;
}

interface PeriodParams extends DateRangeParams {
  group_by?: 'day' | 'week' | 'month';
}

// API Functions
export const statsApi = {
  getDashboardStats: async (params?: DateRangeParams): Promise<DashboardStats> => {
    const response = await api.get('/stats/dashboard', { params });
    return response.data;
  },

  getSalesSummary: async (params?: DateRangeParams): Promise<SalesSummary> => {
    const response = await api.get('/stats/sales/summary', { params });
    return response.data;
  },

  getSalesByPeriod: async (params?: PeriodParams): Promise<SalesByPeriod[]> => {
    const response = await api.get('/stats/sales/by-period', { params });
    return response.data;
  },

  getSalesByPaymentMethod: async (params?: DateRangeParams): Promise<SalesByPaymentMethod[]> => {
    const response = await api.get('/stats/sales/by-payment-method', { params });
    return response.data;
  },

  getTopSellingProducts: async (params?: DateRangeParams & { limit?: number }): Promise<TopSellingProduct[]> => {
    const response = await api.get('/stats/products/top-selling', { params });
    return response.data;
  },

  getSalesByCategory: async (params?: DateRangeParams): Promise<SalesByCategory[]> => {
    const response = await api.get('/stats/products/by-category', { params });
    return response.data;
  },

  getLowStockProducts: async (limit?: number): Promise<LowStockProduct[]> => {
    const response = await api.get('/stats/products/low-stock', { params: { limit } });
    return response.data;
  },

  getNewPatientsByPeriod: async (params?: PeriodParams): Promise<NewPatientsByPeriod[]> => {
    const response = await api.get('/stats/patients/new', { params });
    return response.data;
  },

  getPatientsBySpecies: async (): Promise<PatientsBySpecies[]> => {
    const response = await api.get('/stats/patients/by-species');
    return response.data;
  },

  getServicesSummary: async (params?: DateRangeParams): Promise<ServicesSummary> => {
    const response = await api.get('/stats/services/summary', { params });
    return response.data;
  },

  getGroomingCancellationsByOwner: async (limit: number = 10): Promise<GroomingCancellationByOwner[]> => {
    const response = await api.get('/stats/grooming/cancellations-by-owner', { params: { limit } });
    return response.data;
  },
};
